import { createContext, useContext, useRef, useSyncExternalStore, type ReactNode } from 'react';
import { useStore } from 'zustand';
import type { GameSave, GameServices } from '@/app/services';
import type { Game, PlacementUiState } from '@/game/Game';

interface Ctx {
  services: GameServices;
  game: Game | null;
}

const ServicesContext = createContext<Ctx | null>(null);

export function ServicesProvider({
  services,
  game,
  children,
}: {
  services: GameServices;
  game: Game | null;
  children: ReactNode;
}) {
  return <ServicesContext.Provider value={{ services, game }}>{children}</ServicesContext.Provider>;
}

function useCtx(): Ctx {
  const ctx = useContext(ServicesContext);
  if (!ctx) throw new Error('ServicesProvider missing');
  return ctx;
}

export function useServices(): GameServices {
  return useCtx().services;
}

export function useGame(): Game | null {
  return useCtx().game;
}

/** Subscribe a component to a slice of the save. Keep selectors cheap and return stable values. */
export function useSave<T>(selector: (save: GameSave) => T): T {
  return useStore(useCtx().services.store, selector);
}

/**
 * A value computed from the whole save that only re-renders when the result changes (compared as JSON).
 * For derived views that read too many fields for a slice selector; the 4 Hz clock tick alone never
 * re-renders them (M4-13). `compute` may close over stable things only (content, services).
 */
export function useSaveDerived<T>(compute: (save: GameSave) => T): T {
  const { store } = useCtx().services;
  const cache = useRef<{ save: GameSave; key: string; value: T } | null>(null);
  const snapshot = () => {
    const save = store.getState();
    const c = cache.current;
    if (c?.save === save) return c.value;
    const value = compute(save);
    const key = JSON.stringify(value);
    cache.current = { save, key, value: c?.key === key ? c.value : value };
    return cache.current.value;
  };
  return useSyncExternalStore(store.subscribe, snapshot, snapshot);
}

/** Transient placement state (selection, drag hints) from a running game. */
export function usePlacementUi<T>(game: Game, selector: (ui: PlacementUiState) => T): T {
  return useStore(game.placementUi, selector);
}
